import { useRef, useEffect } from "react";
import { useTrainingTips } from "./useTrainingTips";

/**
 * Custom hook to handle training functionality for any element
 * @param {string} page - The page name for training context
 * @param {string} tipId - The unique tip ID
 * @param {string} indicatorType - Type of training indicator ("button", "section", "form-section", etc.)
 * @param {string} position - Position of the tip relative to element ("top", "bottom", "left", "right")
 * @param {number} delay - Delay before showing tip on hover (default: 300ms)
 * @returns {object} - Object containing ref, classes, and hover handler
 */
export const useTrainingElement = (
  page,
  tipId,
  indicatorType = "button",
  position = "bottom",
  delay = 300
) => {
  const elementRef = useRef(null);
  const {
    showTipForComponent,
    getTrainingIndicatorClass,
    addTrainingIndicator,
    removeTrainingIndicator,
    isTrainingEnabled,
  } = useTrainingTips(page);

  // Manage training indicators
  useEffect(() => {
    if (elementRef.current) {
      if (isTrainingEnabled) {
        addTrainingIndicator(elementRef.current, tipId, indicatorType);
      } else {
        removeTrainingIndicator(elementRef.current);
      }
    }

    // Cleanup on unmount
    return () => {
      if (elementRef.current) {
        removeTrainingIndicator(elementRef.current);
      }
    };
  }, [
    isTrainingEnabled,
    addTrainingIndicator,
    removeTrainingIndicator,
    tipId,
    indicatorType,
  ]);

  // Function to show tip on hover
  const showTip = () => {
    if (elementRef.current) {
      showTipForComponent(tipId, elementRef.current, position, delay);
    }
  };

  return {
    ref: elementRef,
    className: getTrainingIndicatorClass(tipId, indicatorType),
    onMouseEnter: showTip,
    isTrainingEnabled,
  };
};

/**
 * Convenience hook specifically for training buttons
 */
export const useTrainingButton = (page, tipId, position = "bottom") => {
  return useTrainingElement(page, tipId, "button", position);
};

/**
 * Convenience hook specifically for training sections
 */
export const useTrainingSection = (page, tipId, position = "bottom") => {
  return useTrainingElement(page, tipId, "section", position);
};
